import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  fetchPublicTournamentJoinContext,
  requestPublicTournamentJoinOtp,
  verifyPublicTournamentJoinOtp,
} from '../../../services/tournamentService';

const TOURNAMENT_ROLE_OPTIONS = ['Player', 'Scorer', 'Umpire', 'Match Referee'];

const normalizeTournamentRoles = (roles) => (Array.isArray(roles) && roles.length ? roles : ['Player']);

const PublicTournamentJoinPage = () => {
  const { inviteCode } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: '',
    data: null,
    actionMessage: '',
    email: '',
    mobile: '',
    otp: '',
    otpRequested: false,
    selectedTournamentRoles: ['Player'],
    hasUserEditedRoles: false,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchPublicTournamentJoinContext(inviteCode);
        if (!mounted) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          data,
          error: '',
          selectedTournamentRoles: prev.hasUserEditedRoles
            ? prev.selectedTournamentRoles
            : normalizeTournamentRoles(data?.defaultTournamentRoles),
        }));
      } catch (error) {
        if (!mounted) return;
        setState((prev) => ({ ...prev, loading: false, error: error.message || 'Unable to load join page.' }));
      }
    })();
    return () => { mounted = false; };
  }, [inviteCode]);

  const availableTournamentRoles = normalizeTournamentRoles(state.data?.availableTournamentRoles || TOURNAMENT_ROLE_OPTIONS);
  const registrationStatus = state.data?.playerRegistrationStatus || (state.data?.registrationClosed ? 'DISABLED' : 'OPEN');
  const closedMessage = registrationStatus === 'FULL'
    ? { title: 'REGISTRATION FULL', body: 'All available player spots have been filled.' }
    : { title: 'REGISTRATION CLOSED', body: 'Registration for this Tournament has ended.' };

  const toggleTournamentRole = (role) => {
    setState((prev) => ({
      ...prev,
      selectedTournamentRoles: prev.selectedTournamentRoles.includes(role)
        ? prev.selectedTournamentRoles.filter((item) => item !== role)
        : [...prev.selectedTournamentRoles, role],
      hasUserEditedRoles: true,
    }));
  };

  const handleRequestOtp = async () => {
    const hasEmail = Boolean(state.email.trim());
    const hasMobile = Boolean(state.mobile.trim());
    if (!hasEmail && !hasMobile) {
      setState((prev) => ({ ...prev, error: 'Enter email or mobile number to receive OTP.' }));
      return;
    }
    if (!state.selectedTournamentRoles.length) {
      setState((prev) => ({ ...prev, error: 'Select at least one tournament role.' }));
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: '', actionMessage: '' }));
    try {
      const response = await requestPublicTournamentJoinOtp(inviteCode, {
        email: state.email.trim(),
        mobile: state.mobile.trim(),
        tournamentRoles: state.selectedTournamentRoles,
      });
      setState((prev) => ({
        ...prev,
        loading: false,
        otpRequested: true,
        selectedTournamentRoles: !prev.hasUserEditedRoles && Array.isArray(response?.defaultTournamentRoles) && response.defaultTournamentRoles.length
          ? response.defaultTournamentRoles
          : prev.selectedTournamentRoles,
        actionMessage: response?.message || `OTP sent successfully${state.email.trim() && state.mobile.trim() ? ' to email and mobile' : ''}.`,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error: error.message || 'Unable to send OTP.' }));
    }
  };

  const handleVerifyOtp = async () => {
    if (!state.selectedTournamentRoles.length) {
      setState((prev) => ({ ...prev, error: 'Select at least one tournament role.' }));
      return;
    }
    if (!state.otp) {
      setState((prev) => ({ ...prev, error: 'OTP is required.' }));
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: '', actionMessage: '' }));
    try {
      const response = await verifyPublicTournamentJoinOtp(inviteCode, {
        email: state.email.trim(),
        mobile: state.mobile.trim(),
        otp: state.otp.trim(),
        tournamentRoles: state.selectedTournamentRoles,
      });
      const nextMessage = response?.requiresSignup
        ? (response?.message || 'OTP verified. Please complete signup to finish joining.')
        : (response?.message || 'Joined successfully.');
      setState((prev) => ({
        ...prev,
        loading: false,
        actionMessage: nextMessage,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error: error.message || 'Unable to verify OTP.' }));
    }
  };

  if (state.loading) return <div className="container py-5">Loading join details...</div>;

  return (
    <div className="container py-5">
      <h1 className="h4 mb-2">{state.data?.tournament?.name || 'Tournament Join'}</h1>
      {state.error ? <div className="alert alert-danger">{state.error}</div> : null}
      {!state.error && state.data?.registrationClosed ? (
        <div className="alert alert-warning" role="status">
          <strong>{closedMessage.title}</strong>
          <div>{closedMessage.body}</div>
          {state.data?.maxPlayers ? <div>{state.data.registeredCount} / {state.data.maxPlayers} players registered</div> : null}
        </div>
      ) : (
        <div className="card p-3">
          <p className="text-muted mb-3">Enter email or mobile number to receive OTP.</p>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              value={state.email}
              onChange={(event) => setState((prev) => ({ ...prev, email: event.target.value }))}
              disabled={state.loading || state.otpRequested}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mobile</label>
            <input
              type="tel"
              className="form-control"
              value={state.mobile}
              onChange={(event) => setState((prev) => ({ ...prev, mobile: event.target.value }))}
              disabled={state.loading || state.otpRequested}
            />
          </div>
          <section className="mb-3">
            <h2 className="h5 mb-1">Tournament Roles</h2>
            <p className="text-muted mb-2">Select the roles you are available for in this tournament.</p>
            {availableTournamentRoles.map((role) => (
              <div className="form-check" key={role}>
                <input
                  className="form-check-input"
                  id={`tournament-role-${role.replace(/\s+/g, '-').toLowerCase()}`}
                  type="checkbox"
                  checked={state.selectedTournamentRoles.includes(role)}
                  onChange={() => toggleTournamentRole(role)}
                  disabled={state.loading}
                />
                <label className="form-check-label" htmlFor={`tournament-role-${role.replace(/\s+/g, '-').toLowerCase()}`}>
                  {role}
                </label>
              </div>
            ))}
          </section>
          {state.otpRequested ? (
            <>
              <div className="mb-3">
                <label className="form-label">OTP</label>
                <input
                  type="text"
                  className="form-control"
                  value={state.otp}
                  onChange={(event) => setState((prev) => ({ ...prev, otp: event.target.value }))}
                  disabled={state.loading || (!state.email.trim() && !state.mobile.trim())}
                />
              </div>
              <button type="button" className="btn btn-primary" onClick={handleVerifyOtp} disabled={state.loading || (!state.email.trim() && !state.mobile.trim())}>
                Verify OTP & Join
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary" onClick={handleRequestOtp} disabled={state.loading || (!state.email.trim() && !state.mobile.trim())}>
              Send OTP
            </button>
          )}
        </div>
      )}
      {state.actionMessage ? <div className="alert alert-info mt-3 mb-0">{state.actionMessage}</div> : null}
      <div className="mt-3">
        <Link to="/dashboard/individual/tournaments">Go to tournaments</Link>
      </div>
    </div>
  );
};

export default PublicTournamentJoinPage;
